import type { ReactNode } from "react";
import { Hint } from "./Hint";
import { InternalTag } from "./Badge";

/**
 * A box, design.md section 3 as decided on 2026-09-24: a surface with an edge, its title in a head
 * row, its content below. A box never holds another box, so a panel is never passed as the child of
 * a panel; a second list belongs in a second box beside it.
 *
 * `flush` is for a table or a list that draws its own rows to the edge. `internal` marks the box as
 * Petrovic's own (plan.md section 14.6) with the tag and the left edge, not by colour alone.
 */
export interface PanelProps {
  title?: ReactNode;
  /** The heading's id, for a section that is labelled by it. */
  titleId?: string;
  level?: 2 | 3;
  hint?: string;
  actions?: ReactNode;
  foot?: ReactNode;
  flush?: boolean;
  internal?: boolean;
  danger?: boolean;
  className?: string;
  testId?: string;
  children?: ReactNode;
}

export function Panel({
  title,
  titleId,
  level = 2,
  hint,
  actions,
  foot,
  flush = false,
  internal = false,
  danger = false,
  className,
  testId,
  children
}: PanelProps) {
  const Heading = level === 2 ? "h2" : "h3";
  const classes = ["panel", internal ? "is-internal" : "", danger ? "is-danger" : "", className ?? ""]
    .filter(Boolean)
    .join(" ");

  return (
    <section className={classes} aria-labelledby={title && titleId ? titleId : undefined} data-testid={testId}>
      {title || actions ? (
        <header className="panel-head">
          {title ? (
            <Heading id={titleId}>
              {internal ? <InternalTag /> : null}
              {title}
              {hint ? <Hint>{hint}</Hint> : null}
            </Heading>
          ) : (
            <span />
          )}
          {actions ? <div className="panel-actions">{actions}</div> : null}
        </header>
      ) : null}
      {flush ? children : <div className="panel-body">{children}</div>}
      {foot ? <footer className="panel-foot">{foot}</footer> : null}
    </section>
  );
}
